import { useTranslation } from "react-i18next";
import { Route } from "@/routes/visits/$visitId";
import { useCallback, useEffect, useState, useRef } from "react";
import { useVisitStore } from "@/store/visit.store";
import { useAuthStore } from "@/store/auth.store";
import { MultiRecordingSidebar } from "@/components/ui/visits/multi-recording-sidebar";
import { TranscriptionsCard } from "@/components/ui/visits/transcriptions-card";
import { Layout } from "@/components/layout";
import { VisitHeader } from "../../components/ui/visit/VisitHeader";
import { VisitTemplateViewer } from "./components/VisitTemplateViewer";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { getFormattedDateToHour } from "@/lib/time";
import { FormProvider, useForm } from "react-hook-form";
import type { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/hooks/use-toast";
import { useVisitActions } from "@/hooks/useVisitActions";
import { EditTitleVisitSchema } from "@/schemas/editTitleVisit.schema";
import { EditTitleVisitDialogForm } from "./components/EditTitleVisitDialogForm";
import { REFRESH_AUDIO_INTERVAL } from "@/common/config";
import { AppConfig } from "@/common/appConfig";
import { useFileVersionStore } from "@/store/fileVersion.store";
import { useCreateNoteTemplate } from "@/hooks/notesTemplates/useCreateNoteTemplate";

export const Visit = (): JSX.Element => {
  const { t } = useTranslation();
  const { useParams } = Route;
  const { visitId } = useParams<{ visitId: string }>();
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [isMultiRecordingOpen, setIsMultiRecordingOpen] = useState(false);
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [visitTitle, setVisitTitle] = useState<string>("");
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();
  const { getUser, user, userData } = useAuthStore.getState();
  const {
    getFile,
    file,
    notes,
    loading,
    getMultiRecordingFiles,
    multiRecordingFiles,
    reset,
  } = useVisitStore();
  const { reset: resetFileVersion } = useFileVersionStore.getState();

  useEffect(() => {
    return (): void => {
      reset();
      resetFileVersion();
    };
  }, [reset, resetFileVersion]);

  const {
    changeTitleVisitByFile,
    result: changeTitleResult,
    loading: changeTitleLoading,
    error: changeTitleError,
  } = useVisitActions();

  const {
    createNoteTemplate: createNoteTemplateAPI,
    loading: creatingNoteTemplate,
    error: createNoteTemplateError,
    result: createNoteTemplateResult,
  } = useCreateNoteTemplate();

  const previousResultRef = useRef<boolean | null>(null);

  const form = useForm({
    resolver: zodResolver(EditTitleVisitSchema),
    defaultValues: {
      title: `${t("visit.title")}`,
    },
  });

  useEffect(() => {
    if (user?.email) {
      void getUser(user.email);
    }
    if (userData?.id) {
      void getFile({ userId: userData.id, fileId: Number(visitId) });
      void getMultiRecordingFiles({ fileId: Number(visitId) });
      void fetchAndSetAvailableMinutes(userData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (file) {
      const title = file.title || `${t("visit.title")} ${file.rowno}`;
      setVisitTitle(title);
      if (!showEditTitleModal) {
        form.reset({
          title: title,
        });
      }
    }
  }, [file, form, t, showEditTitleModal]);

  useEffect(() => {
    if (!userData) return undefined;
    const intervalId = setInterval(() => {
      void getFile({ userId: userData.id, fileId: Number(visitId) }, false);
      void getMultiRecordingFiles({ fileId: Number(visitId) }, false);
    }, REFRESH_AUDIO_INTERVAL);
    return (): void => {
      clearInterval(intervalId);
    };
  }, [getFile, getMultiRecordingFiles, userData, visitId]);

  const toggleExpanded = (id: number): void => {
    setExpandedItems((previous) =>
      previous.includes(id)
        ? previous.filter((x) => x !== id)
        : [...previous, id]
    );
  };

  const refreshVisit = useCallback(() => {
    if (userData) {
      void getFile({ userId: userData.id, fileId: Number(visitId) }, true);
    }
  }, [userData, getFile, visitId]);

  useEffect(() => {
    if (!changeTitleLoading) {
      if (changeTitleResult) {
        toast({
          variant: "success",
          title: changeTitleResult,
        });
        setShowEditTitleModal(false);
        if (userData) {
          void getFile({ userId: userData.id, fileId: Number(visitId) }, false);
        }
      }
      if (changeTitleError) {
        toast({
          variant: "error",
          title: changeTitleError,
        });
      }
    }
  }, [
    changeTitleResult,
    changeTitleError,
    userData,
    visitId,
    getFile,
    changeTitleLoading,
  ]);

  const handleSubmit = (data: z.infer<typeof EditTitleVisitSchema>): void => {
    if (file) {
      void changeTitleVisitByFile(file, data.title);
    }
  };

  const handleGenerateReferralLetter = useCallback(async (): Promise<void> => {
    if (!file?.transcription || !file.id) {
      toast({
        variant: "error",
        title: t("visit.messages.createFailed"),
        description: "No transcription available",
      });
      return;
    }

    // Show "start processing" toast immediately
    toast({
      variant: "default",
      title: t("visit.messages.startProcessing"),
      description: t("visit.messages.generatingReferralLetter"),
    });

    await createNoteTemplateAPI({
      fileId: file.id,
      template: "Referral Letter",
      transcription: file.transcription,
      style: file.style || "Paragraph",
      format: file.format || "Concise",
      exam: file.exam || "",
      macros: "",
      customTemplateId: null,
    });

    // Refresh immediately to update file status
    if (userData) {
      void getFile({ userId: userData.id, fileId: Number(visitId) }, false);
    }
  }, [file, createNoteTemplateAPI, t, userData, visitId, getFile]);

  const handleGeneratePatientSummary = useCallback(async (): Promise<void> => {
    if (!file?.transcription || !file.id) {
      toast({
        variant: "error",
        title: t("visit.messages.createFailed"),
        description: "No transcription available",
      });
      return;
    }

    // Show "start processing" toast immediately
    toast({
      variant: "default",
      title: t("visit.messages.startProcessing"),
      description: t("visit.messages.generatingPatientSummary"),
    });

    await createNoteTemplateAPI({
      fileId: file.id,
      template: "Patient Summary",
      transcription: file.transcription,
      style: file.style || "Bullet Point",
      format: file.format || "Concise",
      exam: file.exam || "",
      macros: "",
      customTemplateId: null,
    });

    // Refresh immediately to update file status
    if (userData) {
      void getFile({ userId: userData.id, fileId: Number(visitId) }, false);
    }
  }, [file, createNoteTemplateAPI, t, userData, visitId, getFile]);

  useEffect(() => {
    if (!creatingNoteTemplate) {
      if (createNoteTemplateError) {
        toast({
          variant: "error",
          title: t("visit.messages.createFailed"),
        });
      }
      // Only process if result changed from null/false to true
      if (
        createNoteTemplateResult === true &&
        previousResultRef.current !== true &&
        userData &&
        file
      ) {
        previousResultRef.current = true;
        toast({
          variant: "success",
          title: t("visit.messages.createSuccessful"),
        });
        refreshVisit();
      }
    }
    // Update ref when result changes
    if (createNoteTemplateResult !== previousResultRef.current) {
      previousResultRef.current = createNoteTemplateResult;
    }
  }, [
    creatingNoteTemplate,
    createNoteTemplateError,
    createNoteTemplateResult,
    userData,
    file,
    refreshVisit,
    t,
  ]);

  return (
    <Layout
      isLoading={loading}
      navbarTitle={t("visit.title")}
      breadcrumbs={[
        { name: t("visits.title"), href: `/visits`, current: false },
        ...(file?.rowno
          ? [
              {
                name: visitTitle,
                href: `/visits/${visitId}`,
                current: true,
              },
            ]
          : []),
      ]}
    >
      <div className="flex min-h-screen">
        <div className="flex-1 space-y-4 p-8 pt-6">
          <VisitHeader
            enableMultirecording={AppConfig.enableMultirecording}
            file={file}
            title={visitTitle}
            onGeneratePatientSummary={handleGeneratePatientSummary}
            onGenerateReferralLetter={handleGenerateReferralLetter}
            onAddRecording={() => {
              setIsMultiRecordingOpen(true);
            }}
            onEditTitleClick={() => {
              setShowEditTitleModal(true);
            }}
          />
          <div className="flex justify-between">
            <span className="text-base">
              {t("visit.created")}
              {": "}
              {file && getFormattedDateToHour(file?.createDate)}
            </span>
            <span className="opacity-0">{t("visit.lastModified")}</span>
          </div>
          <TranscriptionsCard
            expandedItems={expandedItems}
            file={file}
            multiRecordingFiles={multiRecordingFiles}
            toggleExpanded={toggleExpanded}
          />
          {file && (
            <VisitTemplateViewer
              file={file}
              isRegenerateEnabled={multiRecordingFiles.length < 1}
              notes={notes}
              refresh={refreshVisit}
            />
          )}
        </div>
        <MultiRecordingSidebar
          open={isMultiRecordingOpen}
          onOpenChange={setIsMultiRecordingOpen}
        />
        <FormProvider {...form}>
          <EditTitleVisitDialogForm
            loading={changeTitleLoading}
            open={showEditTitleModal}
            onOpenChange={setShowEditTitleModal}
            onSubmit={handleSubmit}
          />
        </FormProvider>
      </div>
    </Layout>
  );
};
